import { browserTracingIntegration, init, replayIntegration } from '@sentry/angular';
import { environment } from '@env/environment';
import packageInfo from '../package.json';
import type { BrowserOptions } from '@sentry/browser';
import { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint, Integration, SentryXhrData } from '@sentry/core';

/**
 * Init Sentry connection
 */

export function sentryInit(): void {
  const sentryConfig: BrowserOptions = {
    dsn: environment.SENTRY_DSN,
    // https://docs.sentry.io/platforms/javascript/tracing/instrumentation/automatic-instrumentation/
    tracePropagationTargets: [environment.app_uri, environment.apiV2, /^\//],
    // check only our site and ignore 3rd party errors
    allowUrls: [environment.app_uri, environment.apiV2, /^\//],
    integrations: [browserTracingIntegration()],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: environment.production ? 0.5 : 1,
    release: `${environment.SENTRY_PROJECT}@${packageInfo.version}`,
    // Enrich XHR breadcrumbs with request metadata, including the request body.
    beforeBreadcrumb: (breadcrumb: Breadcrumb, hint: BreadcrumbHint) => {
      if (breadcrumb.category === 'xhr') {
        const sentryXhr: SentryXhrData = hint?.xhr?.__sentry_xhr_v3__ || hint?.xhr?.__sentry_xhr__;
        const data = {
          method: sentryXhr?.method,
          requestBody: sentryXhr?.body,
          statusCode: sentryXhr?.status_code,
          url: sentryXhr?.url
        };

        return { ...breadcrumb, data };
      }

      return breadcrumb;
    },
    // we don't need to track events with zero status
    beforeSend(event: ErrorEvent, hint: EventHint) {
      return event.message?.indexOf('0 Unknown Error') !== -1 ? null : event;
    }
  };

  if (environment.production) {
    (sentryConfig.integrations as Integration[]).push(
      replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
        maskAllInputs: true
      })
    );
    // TODO: TT-4395 return with updated options with check for the new users
    // remove replays without errors
    // sentryConfig.replaysSessionSampleRate = 1.0;
    // capture each error in dev/stage modes
    sentryConfig.replaysOnErrorSampleRate = 1.0;
  }

  if (environment.SENTRY_DSN) {
    init(sentryConfig);
  }
}
